'use client'
import { useEffect, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { useActivePatient } from '@/context/ActivePatientContext'
import { usePatientConsultations } from '@/hooks/usePatientConsultations'
import { pharmacyApi } from '@/services/pharmacy.service'
import { PatientMediaCard } from '@/components/patient/PatientMediaCard'
import { ConsultationNoteList } from '@/components/consultation/ConsultationNoteList'
import type { LabFile } from '@/types/file'
import { ActiveConsultationScreen } from '@/components/consultation/ActiveConsultationScreen'
import { DiagnosisSummaryCard } from '@/components/patient/DiagnosisSummaryCard'

export function DiagnosticoTab() {
  const { activePatientData, setActivePatient } = useActivePatient()
  // Single source for progress notes (REQ-VIS-01).
  const { signed, total, isLoading: notesLoading } = usePatientConsultations(
    activePatientData?.id,
  )
  const [labFiles, setLabFiles] = useState<LabFile[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!activePatientData) return
    setLoading(true)
    pharmacyApi
      .getPatientLabFiles(activePatientData.id, 20)
      .then(setLabFiles)
      .catch(() => setLabFiles([]))
      .finally(() => setLoading(false))
  }, [activePatientData?.id])

  if (loading || notesLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-ml-primary" />
      </div>
    )
  }

  if (!activePatientData) return null

  const latestImage = labFiles.find((f) => f.mime_type.startsWith('image/')) ?? null

  return (
    <div className="space-y-6">
      {/* Editable diagnostic summary — latest diagnostic impression + appendable
          history. Lives on the Diagnóstico tab (the Resumen tab is read-only). */}
      <DiagnosisSummaryCard
        patientId={activePatientData.id}
        history={activePatientData.diagnosis_history ?? []}
        onAppended={setActivePatient}
      />

      {/* Monitoring & notes — full signed history, newest first, with per-note
          vital deltas so the patient's progression is visible (REQ-VIS-02c). */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-xs font-bold uppercase tracking-widest text-ml-on-surface-variant">
            Seguimiento y notas
          </h3>
          {total > 0 && (
            <span className="text-[11px] font-medium text-ml-on-surface-variant/70">
              {total} {total === 1 ? 'nota firmada' : 'notas firmadas'}
            </span>
          )}
        </div>
        <ConsultationNoteList
          items={signed}
          variant="full"
          searchable={total > 1}
          emptyText="Sin notas firmadas. Inicia una consulta para crear una nota."
        />
      </div>

      {/* Latest media image */}
      <PatientMediaCard file={latestImage} />

      {/* Active consultation screen */}
      <ActiveConsultationScreen />
    </div>
  )
}
